import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import {
  decideShopSupportAccess,
  describeShopSupportAccessError,
  getShopSupportAccess,
  getShopSupportOptIn,
  revokeShopSupportAccess,
  setShopSupportOptIn,
  SCOPE_LABEL,
  type ShopSupportAccessRow,
  type ShopSupportOptIn,
} from '@/lib/support/shopSupportAccess';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Core-100 R5 CORE-BJ / OD-49 — the shop's side of shop-authorised support access.
 *
 * Every member of the shop sees whether OptiBay support has asked for, holds, or held access, why, under what scope,
 * until when, and how many reads were made. Owners and managers (never the operator who asked) can approve, deny or
 * revoke. The server decides who may do what; buttons only appear where the server said `can_decide`.
 */
const STATUS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  REQUESTED: { label: 'Requested', variant: 'outline' },
  APPROVED: { label: 'Approved — not opened yet', variant: 'default' },
  ACTIVE: { label: 'Active now', variant: 'destructive' },
  EXPIRED: { label: 'Ended (expired)', variant: 'secondary' },
  DENIED: { label: 'Denied', variant: 'secondary' },
  REVOKED: { label: 'Revoked', variant: 'secondary' },
  STALE: { label: 'Request lapsed', variant: 'secondary' },
};

export const ShopSupportAccessPanel = ({ shopId }: { shopId: string }) => {
  const [rows, setRows] = useState<ShopSupportAccessRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [basis, setBasis] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // OD-49: the shop's standing opt-in (undefined = not read yet; null = never decided = not opted in).
  const [optIn, setOptIn] = useState<ShopSupportOptIn | null | undefined>(undefined);
  const [optInError, setOptInError] = useState(false);
  const { canEditShopProfile: canDecideOptIn } = usePermissions();

  const load = useCallback(async () => {
    try {
      setRows(await getShopSupportAccess(shopId));
      setLoadError(null);
    } catch (error) {
      setLoadError(describeShopSupportAccessError(error as { message?: string })
        ?? 'We could not load support access for this shop. This does not mean no access occurred.');
    }
  }, [shopId]);

  // The opt-in is read on its own: failing to read it is said, never shown as "not opted in", and never hides the grants.
  const loadOptIn = useCallback(async () => {
    try {
      setOptIn(await getShopSupportOptIn(shopId));
      setOptInError(false);
    } catch {
      setOptInError(true);
    }
  }, [shopId]);

  useEffect(() => { void load(); void loadOptIn(); }, [load, loadOptIn]);

  const act = async (grantId: string, fn: () => Promise<unknown>) => {
    setBusy(grantId);
    setActionError(null);
    try {
      await fn();
      await load();
    } catch (error) {
      setActionError(describeShopSupportAccessError(error as { message?: string }) ?? 'That did not go through. Nothing was changed.');
    } finally {
      setBusy(null);
    }
  };

  const live = rows?.filter((r) => r.status === 'ACTIVE' || r.status === 'APPROVED') ?? [];

  return (
    <Card data-testid="shop-support-access-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />OptiBay support access to this shop</CardTitle>
        <CardDescription>
          Support can only see your shop when an owner or manager approves it, for at most one hour, read-only. You can
          revoke it at any time. Customers' phone numbers, emails and addresses are never shown to support.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {live.length > 0 && (
          <Alert variant="destructive" data-testid="shop-support-access-live">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              OptiBay support currently holds approved access to this shop
              {live[0].access_expires_at ? ` until ${new Date(live[0].access_expires_at).toLocaleString()}` : ''}.
            </AlertDescription>
          </Alert>
        )}
        {optInError && (
          <p className="text-sm text-destructive" data-testid="shop-support-opt-in-error">
            We could not read whether this shop allows OptiBay support to ask for access.
          </p>
        )}
        {!optInError && optIn !== undefined && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3" data-testid="shop-support-opt-in">
            <p className="text-sm">
              {optIn?.opted_in
                ? 'This shop allows OptiBay support to ask for access. Each request still needs an owner or manager to approve it.'
                : 'This shop has not opted in: OptiBay support cannot ask for access.'}
              {optIn && <span className="block text-xs text-muted-foreground">Decided {new Date(optIn.decided_at).toLocaleString()}{optIn.reason ? ` — ${optIn.reason}` : ''}</span>}
            </p>
            {canDecideOptIn && (
              <Button size="sm" variant="outline" disabled={busy === 'opt-in'} data-testid="shop-support-opt-in-toggle"
                onClick={() => act('opt-in', async () => {
                  await setShopSupportOptIn(shopId, !optIn?.opted_in, optIn?.opted_in ? 'Opted out by the shop' : 'Opted in by the shop');
                  await loadOptIn();
                })}>
                {optIn?.opted_in ? 'Opt out' : 'Allow support to ask'}
              </Button>
            )}
          </div>
        )}
        {actionError && (
          <Alert variant="destructive" data-testid="shop-support-access-action-error">
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}
        {loadError ? (
          <Alert variant="destructive" data-testid="shop-support-access-load-error">
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
        ) : rows === null ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="shop-support-access-empty">
            OptiBay support has never asked for access to this shop.
          </p>
        ) : (
          rows.map((r) => {
            const st = STATUS[r.status] ?? { label: r.status, variant: 'outline' as const };
            const scope = r.granted_scope ?? r.requested_scope;
            return (
              <div key={r.grant_id} className="p-3 rounded-md border border-border space-y-2" data-testid={`shop-support-access-row-${r.grant_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{r.operator_label} asked: {r.request_purpose}</p>
                    <p className="text-xs text-muted-foreground">{SCOPE_LABEL[scope]} · asked {new Date(r.requested_at).toLocaleString()}</p>
                    {r.authorisation_basis && <p className="text-xs text-muted-foreground">Approved because: {r.authorisation_basis}</p>}
                    {r.access_expires_at && <p className="text-xs text-muted-foreground">Ends {new Date(r.access_expires_at).toLocaleString()}</p>}
                    <p className="text-xs text-muted-foreground" data-testid={`shop-support-access-reads-${r.grant_id}`}>
                      Reads by support: {r.read_count}{r.last_read_at ? ` (last ${new Date(r.last_read_at).toLocaleString()})` : ''}
                    </p>
                    {r.revoke_reason && <p className="text-xs text-muted-foreground">Ended: {r.revoke_reason}</p>}
                  </div>
                  <Badge variant={st.variant}>{st.label}</Badge>
                </div>
                {r.can_decide && r.status === 'REQUESTED' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input className="max-w-sm" placeholder="Why the shop allows this (e.g. we asked for help)"
                      aria-label="Authorisation basis" value={basis[r.grant_id] ?? ''}
                      onChange={(e) => setBasis((b) => ({ ...b, [r.grant_id]: e.target.value }))} />
                    <Button size="sm" disabled={busy === r.grant_id}
                      onClick={() => act(r.grant_id, () => decideShopSupportAccess(r.grant_id, 'APPROVE', basis[r.grant_id]))}>
                      Approve for 1 hour
                    </Button>
                    <Button size="sm" variant="outline" disabled={busy === r.grant_id}
                      onClick={() => act(r.grant_id, () => decideShopSupportAccess(r.grant_id, 'DENY'))}>
                      Deny
                    </Button>
                  </div>
                )}
                {r.can_decide && (r.status === 'ACTIVE' || r.status === 'APPROVED' || r.status === 'REQUESTED') && (
                  <Button size="sm" variant="destructive" disabled={busy === r.grant_id}
                    onClick={() => act(r.grant_id, () => revokeShopSupportAccess(r.grant_id, 'Revoked by the shop'))}>
                    Revoke now
                  </Button>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
